import type {
  AlgoliaSearchHelper,
  PlainSearchParameters,
  SearchParameters,
  SearchResults,
} from "algoliasearch-helper";
import {
  BaseHit,
  Connector,
  Hit,
  IndexRenderState,
  Renderer,
  TransformItems,
  UnknownWidgetParams,
  Unmounter,
  WidgetRenderState,
} from "instantsearch.js";
import {
  addAbsolutePosition,
  addQueryID,
  BindEventForHits,
  checkRendering,
  createBindEventForHits,
  createDocumentationMessageGenerator,
  createSendEventForHits,
  escapeHits,
  isEqual,
  SendEventForHits,
  walkIndex,
} from "instantsearch.js/es/lib/utils";
import { safeJsonParse } from "src/utils/helper/json-parser";

/**
 * This mainly uses the original infinite hits connector
 * from instantsearch.js, but adds the ability to cache hits
 * in sessionStorage, and to load previous results.
 */

export type InfiniteHitsCachedHits<THit extends NonNullable<object>> = {
  [page: number]: Array<Hit<THit>>;
};

type Read<THit extends NonNullable<object>> = ({
  state,
}: {
  state: PlainSearchParameters;
}) => InfiniteHitsCachedHits<THit> | null;

type Write<THit extends NonNullable<object>> = ({
  state,
  hits,
}: {
  state: PlainSearchParameters;
  hits: InfiniteHitsCachedHits<THit>;
}) => void;

export type InfiniteHitsCache<THit extends NonNullable<object> = BaseHit> = {
  read: Read<THit>;
  write: Write<THit>;
};

export type InfiniteHitsConnectorParams<
  THit extends NonNullable<object> = BaseHit
> = {
  /**
   * Escapes HTML entities from hits string values.
   *
   * @default `true`
   */
  escapeHTML?: boolean;

  /**
   * Enable the button to load previous results.
   *
   * @default `false`
   */
  showPrevious?: boolean;

  /**
   * Receives the items, and is called before displaying them.
   * Useful for mapping over the items to transform, and remove or reorder them.
   */
  transformItems?: TransformItems<Hit<THit>>;

  /**
   * Reads and writes hits from/to cache.
   * When user comes back to the search page after leaving for product page,
   * this helps restore InfiniteHits and its scroll position.
   */
  cache?: InfiniteHitsCache<THit>;
};

export type InfiniteHitsRenderState<
  THit extends NonNullable<object> = BaseHit
> = {
  /**
   * Loads the previous results.
   */
  showPrevious: () => void;

  /**
   * Loads the next page of hits.
   */
  showMore: () => void;

  /**
   * Indicates whether the first page of hits has been reached.
   */
  isFirstPage: boolean;

  /**
   * Indicates whether the last page of hits has been reached.
   */
  isLastPage: boolean;

  /**
   * Send event to insights middleware
   */
  sendEvent: SendEventForHits;

  /**
   * Returns a string of data-insights-event attribute for insights middleware
   */
  bindEvent: BindEventForHits;

  /**
   * Hits for the current page
   */
  currentPageHits: Array<Hit<THit>>;

  /**
   * Hits for current and cached pages
   * @deprecated use `items` instead
   */
  hits: Array<Hit<THit>>;

  /**
   * Hits for current and cached pages
   */
  items: Array<Hit<THit>>;

  /**
   * The response from the Algolia API.
   */
  results?: SearchResults<Hit<THit>> | null;

  /**
   * Next page based on current loaded pages.
   */
  nextPage: number;

  /**
   * Previous page based on current loaded pages.
   */
  previousPage: number;

  /**
   * If first page is not present in the cache,
   * show the button to load previous results.
   */
  showPreviousButton: boolean;

  /**
   * Loading state of the widget.
   */
  loading: boolean;

  /**
   * Cache size to help render correct amount of skeletons.
   */
  cacheSize: number;

  /**
   * The total number of pages available.
   */
  nbPages: number;
};

const withUsage = createDocumentationMessageGenerator({
  name: "custom-infinite-hits",
  connector: true,
});

export type InfiniteHitsWidgetDescription<
  THit extends NonNullable<object> = BaseHit
> = {
  $$type: "ot.infiniteHits";
  renderState: InfiniteHitsRenderState<THit>;
  indexRenderState: {
    infiniteHits: WidgetRenderState<
      InfiniteHitsRenderState<THit>,
      InfiniteHitsConnectorParams<THit>
    >;
  };
  indexUiState: {
    page: number;
  };
};

export type InfiniteHitsConnector<THit extends NonNullable<object> = BaseHit> =
  Connector<
    InfiniteHitsWidgetDescription<THit>,
    InfiniteHitsConnectorParams<THit>
  >;

function isConsecutive(numbers: number[]): boolean {
  const minNumber: number = Math.min(...numbers);
  const maxNumber: number = Math.max(...numbers);

  return maxNumber === minNumber + numbers.length - 1;
}

function getStateWithoutPage(state: PlainSearchParameters) {
  const { page, ...rest } = state || {};
  return rest;
}

function normalizeState(state: PlainSearchParameters) {
  const { clickAnalytics, userToken, ...rest } = state || {};
  return rest;
}

function getInMemoryCache<
  THit extends NonNullable<object>
>(): InfiniteHitsCache<THit> {
  let cachedHits: InfiniteHitsCachedHits<THit> | null = null;
  let cachedState: PlainSearchParameters | null = null;

  if (typeof window !== "undefined") {
    try {
      const cachedData = sessionStorage.getItem("ot-custom-infinite-hits");
      if (cachedData) {
        const cache = safeJsonParse<{
          hits: InfiniteHitsCachedHits<THit> | null;
          state: PlainSearchParameters | null;
        }>(cachedData);

        cachedState = cache?.state || null;
        cachedHits = cache?.hits || null;
      }
    } catch (error) {
      console.error(
        "An error occurred while retrieving infinite hits from the sessionStorage",
        error
      );
    }
  }

  return {
    read({ state }) {
      return isEqual(cachedState, getStateWithoutPage(state)) &&
        isConsecutive(Object.keys(cachedHits || {}).map((key) => Number(key)))
        ? cachedHits
        : null;
    },
    write({ state, hits }) {
      cachedHits = hits;
      cachedState = getStateWithoutPage(state);

      if (typeof window !== "undefined") {
        try {
          sessionStorage.setItem(
            "ot-custom-infinite-hits",
            JSON.stringify({
              state: cachedState,
              hits: cachedHits,
            })
          );
        } catch (error) {
          console.error(
            "An error occurred while saving infinite hits to the sessionStorage",
            error
          );
        }
      }
    },
  };
}

function extractHitsFromCachedHits<THit extends NonNullable<object>>(
  cachedHits: InfiniteHitsCachedHits<THit>
) {
  return Object.keys(cachedHits)
    .map(Number)
    .sort((a, b) => a - b)
    .reduce((acc: Array<Hit<THit>>, page) => {
      return acc.concat(cachedHits[page]);
    }, []);
}

export default (function connectCustomInfiniteHits<
  TWidgetParams extends UnknownWidgetParams
>(
  renderFn: Renderer<InfiniteHitsRenderState, TWidgetParams>,
  unmountFn: Unmounter = () => {}
) {
  checkRendering(renderFn, withUsage());

  return <THit extends NonNullable<object> = BaseHit>(
    widgetParams: TWidgetParams & InfiniteHitsConnectorParams<THit>
  ) => {
    const {
      // @MAJOR: this can default to false
      escapeHTML = true,
      transformItems = ((items) => items) as NonNullable<
        InfiniteHitsConnectorParams<THit>["transformItems"]
      >,
      cache = getInMemoryCache<THit>(),
    } = widgetParams || {};
    let sendEvent: SendEventForHits;
    let bindEvent: BindEventForHits;

    const getFirstReceivedPage = (
      state: SearchParameters,
      cachedHits: InfiniteHitsCachedHits<THit>
    ) => {
      const { page = 0 } = state;

      const pages = Object.keys(cachedHits).map(Number);

      return pages.length > 0 ? Math.min(page, ...pages) : page;
    };

    const getLastReceivedPage = (
      state: SearchParameters,
      cachedHits: InfiniteHitsCachedHits<THit>
    ) => {
      const { page = 0 } = state;

      const pages = Object.keys(cachedHits).map(Number);

      return pages.length > 0 ? Math.max(page, ...pages) : page;
    };

    const getShowPrevious =
      (helper: AlgoliaSearchHelper): (() => void) =>
      () => {
        // Using the helper's `overrideStateWithoutTriggeringChangeEvent` method
        // avoid updating the browser URL when the user displays the previous page.
        helper
          .overrideStateWithoutTriggeringChangeEvent({
            ...helper.state,
            page:
              getFirstReceivedPage(
                helper.state,
                cache.read({ state: normalizeState(helper.state) }) || {}
              ) - 1,
          })
          .searchWithoutTriggeringOnStateChange();
      };

    const getShowMore =
      (helper: AlgoliaSearchHelper): (() => void) =>
      () => {
        helper
          .setPage(
            getLastReceivedPage(
              helper.state,
              cache.read({ state: normalizeState(helper.state) }) || {}
            ) + 1
          )
          .search();
      };

    return {
      $$type: "ot.infiniteHits",

      init(initOptions) {
        renderFn(
          {
            ...this.getWidgetRenderState(initOptions),
            instantSearchInstance: initOptions.instantSearchInstance,
          },
          true
        );
      },

      render(renderOptions) {
        const { instantSearchInstance } = renderOptions;

        const widgetRenderState = this.getWidgetRenderState(renderOptions);

        renderFn(
          {
            ...widgetRenderState,
            instantSearchInstance,
          },
          false
        );

        sendEvent("view:internal", widgetRenderState.currentPageHits);
      },

      getRenderState(
        renderState,
        renderOptions
        // Type is explicitly redefined, to avoid having the TWidgetParams type in the definition
      ): IndexRenderState & InfiniteHitsWidgetDescription["indexRenderState"] {
        return {
          ...renderState,
          infiniteHits: this.getWidgetRenderState(renderOptions),
        };
      },

      getWidgetRenderState({
        results,
        helper,
        parent,
        state: existingState,
        instantSearchInstance,
      }) {
        let isFirstPage: boolean;
        let currentPageHits: Array<Hit<THit>> = [];
        /**
         * We bail out of optimistic UI here, as the cache is based on search
         * parameters, and we don't want to invalidate the cache when the search
         * is loading.
         */
        const state = parent.getPreviousState() || existingState;

        const cachedHits = cache.read({ state: normalizeState(state) }) || {};

        if (!results) {
          sendEvent = createSendEventForHits({
            instantSearchInstance,
            helper,
            widgetType: this.$$type,
          });
          bindEvent = createBindEventForHits({
            helper,
            widgetType: this.$$type,
            instantSearchInstance,
          });
          isFirstPage =
            state.page === undefined ||
            getFirstReceivedPage(state, cachedHits) === 0;
        } else {
          const { page = 0 } = state;

          if (escapeHTML && results.hits.length > 0) {
            results.hits = escapeHits(results.hits);
          }

          const hitsWithAbsolutePosition = addAbsolutePosition(
            results.hits,
            results.page,
            results.hitsPerPage
          );

          const hitsWithAbsolutePositionAndQueryID = addQueryID(
            hitsWithAbsolutePosition,
            results.queryID
          );

          const transformedHits = transformItems(
            hitsWithAbsolutePositionAndQueryID as Hit<THit>[],
            { results }
          );

          /*
              With dynamic widgets, facets are not included in the state before their relevant widgets are mounted. Until then, we need to bail out of writing this incomplete state representation in cache.
            */
          let hasDynamicWidgets = false;
          walkIndex(instantSearchInstance.mainIndex, (indexWidget) => {
            if (
              !hasDynamicWidgets &&
              indexWidget
                .getWidgets()
                .some(({ $$type }) => $$type === "ais.dynamicWidgets")
            ) {
              hasDynamicWidgets = true;
            }
          });

          const hasNoFacets =
            !state.disjunctiveFacets?.length &&
            !(state.facets || []).filter((f) => f !== "*").length &&
            !state.hierarchicalFacets?.length;

          if (
            results.page === state.page &&
            cachedHits[page] === undefined &&
            !results.__isArtificial &&
            instantSearchInstance.status === "idle" &&
            !(hasDynamicWidgets && hasNoFacets)
          ) {
            cachedHits[page] = transformedHits;

            cache.write({ state: normalizeState(state), hits: cachedHits });
          }
          currentPageHits = transformedHits;

          isFirstPage = getFirstReceivedPage(state, cachedHits) === 0;
        }

        const items =
          results?.page === state.page &&
          Object.keys(cachedHits).includes(`${results?.page || 0}`)
            ? extractHitsFromCachedHits(cachedHits)
            : currentPageHits;
        const isLastPage = results
          ? results.nbPages <= getLastReceivedPage(state, cachedHits) + 1
          : true;

        const nextPage =
          results && results.nbPages > 0
            ? getLastReceivedPage(state, cachedHits) + 1
            : 0;
        const previousPage =
          results && results.nbPages > 0
            ? getFirstReceivedPage(state, cachedHits) + 1
            : 0;

        return {
          hits: items,
          items,
          currentPageHits,
          sendEvent,
          bindEvent,
          results: results ?? undefined,
          showPrevious: helper ? getShowPrevious(helper) : () => {},
          showMore: helper ? getShowMore(helper) : () => {},
          isFirstPage,
          isLastPage,
          nextPage,
          previousPage,
          widgetParams,
          nbPages: results?.nbPages || 0,
          loading:
            (instantSearchInstance.status !== "idle" &&
              !results?.__isArtificial) ||
            (instantSearchInstance.status !== "idle" &&
              instantSearchInstance.status !== "error") ||
            state.page !== results?.page,
          cacheSize: Object.keys(cachedHits).length,
          showPreviousButton:
            Object.keys(cachedHits).length > 0 &&
            !Object.keys(cachedHits).includes("0"),
        };
      },

      dispose({ state }) {
        unmountFn();

        const stateWithoutPage = state.setQueryParameter("page", undefined);

        return stateWithoutPage;
      },

      getWidgetUiState(uiState, { searchParameters }) {
        const page = searchParameters.page || 0;

        if (!page) {
          // return without adding `page` to uiState
          // because we don't want `page=1` in the URL
          return uiState;
        }

        return {
          ...uiState,
          // The page in the UI state is incremented by one
          // to expose the user value (not `0`).
          page: page + 1,
        };
      },

      getWidgetSearchParameters(searchParameters, { uiState }) {
        const widgetSearchParameters = searchParameters;

        // The page in the search parameters is decremented by one
        // to get to the actual parameter value from the UI state.
        const page = uiState.page ? uiState.page - 1 : 0;

        return widgetSearchParameters.setQueryParameter("page", page);
      },
    };
  };
} satisfies InfiniteHitsConnector);
